import React, { useState, ChangeEvent, useEffect } from "react"
import { mdiFileCheckOutline, mdiFileAlertOutline } from "@mdi/js"
import Radium from "radium"

// Assuming paths - adjust if these are incorrect
import FileSelectorButton from "../BaseComponents/Buttons/FileSelectorButton"
import ButtonGrey from "../BaseComponents/Buttons/ButtonGrey"
import ButtonBlue from "../BaseComponents/Buttons/ButtonBlue"
import AlertModalOneButton from "./AlertModalOneButton"
import PickerContained from "../BaseComponents/Pickers/PickerContained"
import { ColorPalette } from "../../config"

const getInitialClassification = (fileClassificationOptions?: string[], defaultClassification?: string): string => {
  if (defaultClassification && fileClassificationOptions?.includes(defaultClassification)) {
    return defaultClassification
  }
  return fileClassificationOptions && fileClassificationOptions.length > 0 ? fileClassificationOptions[0] : ""
}

export interface FileSelectionModalProps {
  open: boolean
  title: string
  targetFileFormat: string
  instructionMessage?: string
  classificationLabel?: string
  fileClassificationOptions?: string[]
  defaultClassification?: string
  onCancel: () => void
  onSubmit: (file: File, classification: string) => void
}

const FileSelectionModal: React.FC<FileSelectionModalProps> = (props) => {
  const {
    open,
    title,
    targetFileFormat,
    instructionMessage,
    onCancel,
    onSubmit,
    fileClassificationOptions,
    defaultClassification,
  } = props

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)
  const [selectedClassification, setSelectedClassification] = useState<string>(() =>
    getInitialClassification(fileClassificationOptions, defaultClassification),
  )
  const [warningModalState, setWarningModalState] = useState({
    open: false,
    header: "",
    message: "",
  })

  useEffect(() => {
    if (!open) {
      setSelectedFile(undefined)
      setWarningModalState({ open: false, header: "", message: "" })
      setSelectedClassification(getInitialClassification(fileClassificationOptions, defaultClassification))
    }
  }, [open, defaultClassification, fileClassificationOptions])

  const displayWarning = (header: string, message: string) => {
    setWarningModalState({
      open: true,
      header: header,
      message: message,
    })
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setWarningModalState({ open: false, header: "", message: "" })
    const file = event.target.files?.[0]

    if (file) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase()
      const targetFormatLower = targetFileFormat.toLowerCase()

      if (fileExtension !== targetFormatLower) {
        const header = "Invalid File Type"
        const message = `Select a .${targetFormatLower.toUpperCase()} file.`
        displayWarning(header, message)
        setSelectedFile(undefined)
        if (event.target) {
          event.target.value = ""
        }
        return
      }
      setSelectedFile(file)
    } else {
      setSelectedFile(undefined)
    }
  }

  const handleClassificationChange = (event: { target: { value: string } }) => {
    setSelectedClassification(event.target.value)
  }

  const handleSubmit = () => {
    if (!selectedFile) {
      displayWarning("File Not Selected", "Please select a file before submitting.")
      return
    }
    if (!selectedClassification && fileClassificationOptions && fileClassificationOptions.length > 0) {
      displayWarning("Classification Not Selected", "Please select a file classification before submitting.")
      return
    }
    onSubmit(selectedFile, selectedClassification)
  }

  const getFileSelectorLabel = () => {
    return selectedFile ? "Change selected file" : `Select .${targetFileFormat.toUpperCase()} File`
  }

  let instructionsElement = null
  if (instructionMessage) {
    instructionsElement = <p style={styles.instructionMessage}>{instructionMessage}</p>
  }

  if (!open) {
    return null
  }

  if (warningModalState.open) {
    return (
      <AlertModalOneButton
        open={warningModalState.open}
        header={warningModalState.header}
        body={warningModalState.message}
        buttonLabel={"OK"}
        onClick={() => setWarningModalState({ open: false, header: "", message: "" })}
        dismiss={() => setWarningModalState({ open: false, header: "", message: "" })}
      />
    )
  }

  let pickerElement = null
  if (fileClassificationOptions && fileClassificationOptions.length > 0) {
    pickerElement = (
      <PickerContained
        label={props.classificationLabel || "Select File Category"}
        value={selectedClassification}
        items={fileClassificationOptions}
        onChange={handleClassificationChange}
        style={styles.pickerStyle}
      />
    )
  }

  return (
    <div style={styles.screenContainer}>
      <div style={styles.cardContainer}>
        <h1 style={{ ...styles.textStyle, fontSize: "1.2rem" }}>{title}</h1>

        {instructionsElement}
        {pickerElement}

        <FileSelectorButton
          id="fileSelectionInputModal"
          buttonLabel={getFileSelectorLabel()}
          fileName={selectedFile ? selectedFile.name : "No file selected"}
          fileHandler={handleFileChange as (e: ChangeEvent<{ value: string }>) => void}
          onCancelSelection={() => {
            setSelectedFile(undefined)
            setWarningModalState({ open: false, header: "", message: "" })
          }}
          iconPath={selectedFile ? mdiFileCheckOutline : mdiFileAlertOutline}
          style={styles.selector}
        />

        <div style={styles.actionButtons}>
          <ButtonGrey onClick={onCancel} data-testid="close-button">
            Close
          </ButtonGrey>
          <ButtonBlue onClick={handleSubmit} data-testid="done-button">
            Done
          </ButtonBlue>
        </div>
      </div>
    </div>
  )
}

const styles = {
  screenContainer: {
    position: "absolute" as "absolute",
    top: 0,
    left: 0,
    backgroundColor: ColorPalette.MODAL_BACKGROUND_OVERLAY,
    width: "100vw",
    height: "100vh",
    zIndex: 1000,
    overflow: "hidden",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    flexDirection: "column" as "column",
  },
  cardContainer: {
    zIndex: 100001,
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "flex-start",
    alignItems: "center" as "center",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
    backgroundColor: ColorPalette.CARD_WHITE,
    marginTop: "15vh",
    width: "32%",
    minWidth: "300px",
    maxWidth: "500px",
    padding: "30px 35px 30px 35px",
    borderRadius: 8,
  },
  textStyle: {
    textAlign: "center" as "center",
    alignSelf: "center",
    fontFamily: "roboto",
    fontWeight: "bold",
    color: ColorPalette.PRIMARY_TEXT,
    fontSize: "1rem",
  },
  buttonIconStyle: {
    marginRight: 5,
    width: 15,
    height: 15,
  },
  actionButtons: {
    display: "flex",
    justifyContent: "space-between",
    alignContent: "center",
    marginTop: 20,
    gap: 20,
    width: "100%",
  },
  pickerStyle: {
    width: "90%",
    marginBottom: "1em",
    marginTop: 20,
  },
  instructionMessage: {
    textAlign: "center" as "center",
    color: ColorPalette.PRIMARY_TEXT,
    marginTop: "0px",
    marginBottom: "15px",
  },
  selector: {
    width: "90%",
    height: 200,
    marginTop: "1em",
    marginBottom: "1em",
  },
}

export default Radium(FileSelectionModal)
